import { posthog } from "@/posthog";
import { Authentication } from "@/schema/authentication";
import { Organization } from "@/schema/organization";
import { CustomerProfile } from "@/schema/profile";
import { bearerTokenStorageKey, refreshTokenStorageKey } from "@/config/prefs";

export function handleLogout() {
  removeAuthentication();
  posthog.reset();
  window.location.href = "/auth";
}

export function isUserOnboarded(profile: CustomerProfile | null, organization: Organization | null) {
  if (!profile || !profile.onboarded_at || !organization) {
    return false;
  } else {
    return true;
  }
}

export function persistAuthentication(authentication: Authentication) {
  localStorage.setItem(bearerTokenStorageKey, authentication.access_token);
  localStorage.setItem(refreshTokenStorageKey, authentication.refresh_token);
}

export function removeAuthentication() {
  localStorage.removeItem(bearerTokenStorageKey);
  localStorage.removeItem(refreshTokenStorageKey);
}

export function extractAuthentication() {
  const accessToken = localStorage.getItem(bearerTokenStorageKey);
  const refreshToken = localStorage.getItem(refreshTokenStorageKey);
  return { accessToken, refreshToken };
}

/**
 * Normalizes a website URL by:
 * - Converting to lowercase
 * - Validating the format: (http(s)://)(subdomain).domain-name.(top-level-domain)
 * - Adding https:// prefix if missing
 *
 * @param website - The website URL to normalize
 * @returns The normalized website URL, or the original string if validation fails
 */
export function normalizeWebsite(website: string): string {
  if (!website) return website;

  // Convert to lowercase
  const lowercased = website.toLowerCase().trim();

  // Regex pattern to match: (http(s)://)?(subdomain.)?domain-name.top-level-domain
  // - Optional: http:// or https://
  // - Optional: subdomain (one or more alphanumeric/hyphen segments followed by a dot)
  // - Required: domain-name (one or more alphanumeric/hyphen segments)
  // - Required: top-level-domain (one or more alphanumeric/hyphen/dot segments)
  const websitePattern = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)*([a-zA-Z0-9-]+)\.([a-zA-Z0-9.-]+)$/;

  // Check if the website matches the required format
  if (!websitePattern.test(lowercased)) {
    return website; // Return original if validation fails
  }

  // Add https:// if it doesn't start with http:// or https://
  if (!lowercased.startsWith("http://") && !lowercased.startsWith("https://")) {
    return `https://${lowercased}`;
  }

  return lowercased;
}
